import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Copy, Share2 } from 'lucide-react';
import CapabilityRenderer from './renderers/CapabilityRenderer';
import { buildMiShareText, getMessagePlainText, GPH_LANDING_URL } from './miMessageText';

// Feed da conversa: mensagens do usuário, mensagens do Mi, chips e entregas ricas.
// Comporta-se como um app de IA especializado — não como dashboard.
// Motion de entrada suave por mensagem, com prefers-reduced-motion respeitado
// (o CSS global neutraliza as animações; aqui mantemos transições curtas).

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

const enter = (reduce) => ({
  initial: reduce ? { opacity: 0 } : { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: reduce ? 0.01 : 0.28, ease: [0.22, 1, 0.36, 1] }
});

const writeTextToClipboard = async (text) => {
  if (!text) return;
  const browserNavigator = typeof navigator !== 'undefined' ? navigator : null;
  if (browserNavigator?.clipboard?.writeText) {
    await browserNavigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === 'undefined') throw new Error('Clipboard unavailable');

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

export default function GphConversationCanvas({ messages = [], thinking = false, agent }) {
  const endRef = useRef(null);
  const feedbackTimerRef = useRef(null);
  const [feedback, setFeedback] = useState(null);
  const reduce = prefersReducedMotion();

  useEffect(() => () => {
    if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
  }, []);

  useEffect(() => {
    const anchor = endRef.current;
    if (!anchor) return undefined;

    let cancelled = false;
    const behavior = reduce ? 'auto' : 'smooth';

    const scrollToBottom = async () => {
      if (cancelled) return;

      const ionContent = anchor.closest?.('ion-content');
      if (ionContent?.getScrollElement) {
        const scrollElement = await ionContent.getScrollElement();
        if (!cancelled && scrollElement) {
          scrollElement.scrollTo({
            top: scrollElement.scrollHeight,
            behavior
          });
        }
        return;
      }

      anchor.scrollIntoView({ behavior, block: 'end' });
    };

    const frameId = window.requestAnimationFrame(scrollToBottom);
    const settleId = window.setTimeout(scrollToBottom, reduce ? 0 : 180);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(settleId);
    };
  }, [messages.length, thinking, reduce]);

  const showFeedback = (id, label) => {
    if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    setFeedback({ id, label });
    feedbackTimerRef.current = window.setTimeout(() => setFeedback(null), 1800);
  };

  const copyMessage = async (message) => {
    const actionId = `${message.id}:copy`;
    try {
      await writeTextToClipboard(getMessagePlainText(message));
      showFeedback(actionId, 'Copiado');
    } catch (error) {
      showFeedback(actionId, 'Nao foi possivel copiar');
    }
  };

  const shareMessage = async (message) => {
    const actionId = `${message.id}:share`;
    const sharedText = buildMiShareText(message);

    try {
      const browserNavigator = typeof navigator !== 'undefined' ? navigator : null;
      if (browserNavigator?.share) {
        await browserNavigator.share({
          title: 'Game Pro Hub',
          text: getMessagePlainText(message),
          url: GPH_LANDING_URL
        });
        showFeedback(actionId, 'Compartilhado');
        return;
      }

      await writeTextToClipboard(sharedText);
      showFeedback(actionId, 'Texto copiado');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      try {
        await writeTextToClipboard(sharedText);
        showFeedback(actionId, 'Texto copiado');
      } catch (_) {
        showFeedback(actionId, 'Nao foi possivel compartilhar');
      }
    }
  };

  const renderMessageActions = (message) => {
    const plainText = getMessagePlainText(message);
    if (!plainText) return null;

    const isAssistant = message.role === 'gph';
    const copyActionId = `${message.id}:copy`;
    const shareActionId = `${message.id}:share`;

    return (
      <div className="gph-msg__actions" aria-label="Acoes da mensagem">
        <button
          type="button"
          className="gph-msg-action"
          aria-label={isAssistant ? 'Copiar resposta da Mi' : 'Copiar sua mensagem'}
          title="Copiar"
          onClick={() => copyMessage(message)}
        >
          {feedback?.id === copyActionId ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          <span>{feedback?.id === copyActionId ? feedback.label : 'Copiar'}</span>
        </button>
        {isAssistant && (
          <button
            type="button"
            className="gph-msg-action"
            aria-label="Compartilhar resposta da Mi"
            title="Compartilhar"
            onClick={() => shareMessage(message)}
          >
            {feedback?.id === shareActionId ? <Check aria-hidden="true" /> : <Share2 aria-hidden="true" />}
            <span>{feedback?.id === shareActionId ? feedback.label : 'Compartilhar'}</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="gph-canvas" role="log" aria-live="polite">
      {messages.map((message) => {
        if (message.kind === 'text') {
          return (
            <motion.div
              key={message.id}
              {...enter(reduce)}
              className={`gph-msg ${message.role === 'user' ? 'gph-msg--user' : 'gph-msg--gph'}`}
            >
              {message.role === 'gph' && <span className="gph-msg__author">Mi</span>}
              <p className="gph-msg__text">{message.text}</p>
              {renderMessageActions(message)}
            </motion.div>
          );
        }

        if (message.kind === 'notice') {
          return (
            <motion.div key={message.id} {...enter(reduce)} className="gph-msg gph-msg--notice">
              <p className="gph-msg__text">{message.text}</p>
              {renderMessageActions(message)}
            </motion.div>
          );
        }

        if (message.kind === 'chips') {
          return (
            <motion.div key={message.id} {...enter(reduce)} className="gph-msg gph-msg--gph gph-msg--chips">
              {message.text && (
                <>
                  <span className="gph-msg__author">Mi</span>
                  <p className="gph-msg__text">{message.text}</p>
                </>
              )}
              <div className="gph-chip-row" role="list">
                {(message.chips || []).map((chip, index) => (
                  <motion.button
                    key={`${message.id}-${chip.value}`}
                    type="button"
                    role="listitem"
                    initial={reduce ? { opacity: 0 } : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: reduce ? 0.01 : 0.22, delay: reduce ? 0 : 0.03 * index }}
                    className={`gph-chip ${message.selectedValue === chip.value ? 'gph-chip--selected' : ''}`}
                    disabled={message.resolved}
                    onClick={() => agent.selectChip(message, chip)}
                  >
                    {chip.label}
                  </motion.button>
                ))}
              </div>
              {renderMessageActions(message)}
            </motion.div>
          );
        }

        if (message.kind === 'rich') {
          return (
            <motion.div
              key={message.id}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.99 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: reduce ? 0.01 : 0.34, ease: [0.22, 1, 0.36, 1] }}
              className="gph-msg gph-msg--rich"
            >
              <CapabilityRenderer
                adapterKey={message.adapterKey}
                capabilityId={message.capabilityId}
                context={message.context}
                agent={agent}
              />
              {renderMessageActions(message)}
            </motion.div>
          );
        }

        return null;
      })}

      {(() => {
        const isBusy = Boolean(
          thinking ||
          (agent?.host?.coachStatus && ['uploading', 'queued', 'processing'].includes(agent.host.coachStatus.status)) ||
          (agent?.host?.sensiStatus && ['uploading', 'queued', 'processing'].includes(agent.host.sensiStatus.status))
        );
        if (!isBusy) return null;

        return (
          <motion.div {...enter(reduce)} className="gph-msg gph-msg--gph" aria-label="Resposta em andamento">
            <div className="gph-thinking" role="status" aria-live="polite">
              <div className="gph-thinking__dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
            </div>
          </motion.div>
        );
      })()}

      <div className="gph-canvas__bottom-spacer" aria-hidden="true" />
      <div ref={endRef} aria-hidden="true" />
    </div>
  );
}
